import React from 'react';
import { useKYCWizard, KYC_STEPS } from './KYCWizardContext';
import {
  User,
  MapPin,
  Briefcase,
  GraduationCap,
  FileText,
  Shield,
  Building,
  DollarSign,
  TrendingUp,
  CheckSquare,
  Check,
  AlertCircle,
  ChevronRight,
  Eye
} from 'lucide-react';

// TASK-373: 10-step icons — DocumentUpload removed (now in KYCWorkspace)
const STEP_ICONS = [
  User,          // 1. Personal Information
  MapPin,        // 2. Contact Details
  Briefcase,     // 3. Employment
  GraduationCap, // 4. Personal Background
  FileText,      // 5. Tax Information
  Shield,        // 6. Adverse Media & PEP
  Building,      // 7. Business Activities
  DollarSign,    // 8. Wealth Sources
  TrendingUp,    // 9. Income Sources
  CheckSquare    // 10. Review & Submit
];

const KYCStepNavigation: React.FC = () => {
  const {
    currentStep,
    completedSteps,
    reviewedSteps,
    reviewMode,
    errors,
    goToStep,
    canNavigateToStep,
    reviewComments,
  } = useKYCWizard();

  return (
    <div className="h-full bg-kielis-glacier">
      <div className="p-6">
        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
          Form Sections
        </h2>

        <nav className="space-y-1" role="navigation" aria-label="KYC form sections">
          {KYC_STEPS.map((step, index) => {
            const stepNumber = index + 1;
            const Icon = STEP_ICONS[index];
            const isActive = currentStep === stepNumber;
            const isCompleted = completedSteps.has(stepNumber);
            const isReviewed = reviewedSteps.has(stepNumber);
            const hasErrors = errors[step.section];
            const isClickable = canNavigateToStep(stepNumber);
            const sectionCommentCount = reviewComments.filter(
              (c) => c.section_id === step.section && !c.is_resolved
            ).length;

            // In review mode, use reviewed status; in normal mode, use completed status
            const isDone = reviewMode ? isReviewed : isCompleted;

            return (
              <button
                key={step.id}
                onClick={() => goToStep(stepNumber)}
                disabled={!isClickable}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg
                  transition-all duration-200 group
                  ${isActive
                    ? 'bg-kielis-navy text-white shadow-md'
                    : isDone
                    ? reviewMode
                      ? 'bg-white text-gray-700 hover:bg-gray-50 border border-blue-200'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-green-200'
                    : isClickable
                    ? 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                  }
                `}
                aria-current={isActive ? 'step' : undefined}
                aria-label={`${step.name} - Step ${stepNumber} of ${KYC_STEPS.length}`}
              >
                {/* Step number / completion indicator */}
                <div className="flex-shrink-0">
                  {isDone ? (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      reviewMode ? 'bg-blue-500' : 'bg-green-500'
                    }`}>
                      {reviewMode ? (
                        <Eye className="w-4 h-4 text-white" />
                      ) : (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                  ) : reviewMode ? (
                    // In review mode, show icon instead of number
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center
                      ${isActive
                        ? 'bg-kielis-gold'
                        : 'bg-gray-200'
                      }
                    `}>
                      <Icon className={`w-4 h-4 ${isActive ? 'text-kielis-navy' : 'text-gray-600'}`} />
                    </div>
                  ) : (
                    // In normal mode, show step number
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm
                      ${isActive
                        ? 'bg-kielis-gold text-kielis-navy'
                        : 'bg-gray-200 text-gray-600'
                      }
                    `}>
                      {stepNumber}
                    </div>
                  )}
                </div>

                {/* Icon and label */}
                <div className="flex-1 flex items-center gap-3">
                  <Icon className={`
                    w-5 h-5
                    ${isActive ? 'text-kielis-gold' : ''}
                  `} />
                  <span className="font-medium text-sm text-left">
                    {step.name}
                  </span>
                  {sectionCommentCount > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      {sectionCommentCount}
                    </span>
                  )}
                </div>

                {/* Status indicators */}
                <div className="flex items-center gap-2">
                  {hasErrors && (
                    <AlertCircle className="w-4 h-4 text-red-500" aria-label="This section has errors" />
                  )}
                  {isActive && (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  {isDone && !isActive && (
                    <span className={`text-xs font-medium ${
                      reviewMode ? 'text-blue-600' : 'text-green-600'
                    }`}>
                      {reviewMode ? 'Reviewed' : 'Complete'}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </nav>

        {/* Summary section */}
        <div className="mt-8 p-4 bg-white rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            {reviewMode ? 'Review Progress' : 'Progress Summary'}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">
                {reviewMode ? 'Reviewed' : 'Completed'}
              </span>
              <span className="font-medium text-gray-700">
                {reviewMode ? reviewedSteps.size : completedSteps.size} / {KYC_STEPS.length}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Remaining</span>
              <span className="font-medium text-gray-700">
                {KYC_STEPS.length - (reviewMode ? reviewedSteps.size : completedSteps.size)}
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    reviewMode ? 'bg-blue-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${((reviewMode ? reviewedSteps.size : completedSteps.size) / KYC_STEPS.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Help text */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-700">
            <strong>Tip:</strong> {reviewMode
              ? 'Navigate through each section to review the extracted data. You can edit any field before submitting.'
              : 'You can navigate between completed sections at any time. Save your progress regularly to avoid losing data.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default KYCStepNavigation;